import { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { preventEnterSubmit } from '@/lib/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { HelpArticle, HelpCategory } from '@/types/help-center';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ImageIcon, UploadIcon, CopyIcon, CheckIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface ArticleEditorProps {
  article: HelpArticle | null;
  categories: HelpCategory[];
  open: boolean;
  onClose: () => void;
}

interface ArticleFormData {
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  category_id: string;
  tags: string;
}

interface UploadedImage {
  name: string;
  url: string;
}

export function ArticleEditor({ article, categories, open, onClose }: ArticleEditorProps) {
  const queryClient = useQueryClient();
  const isEditing = !!article;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ArticleFormData>({
    defaultValues: {
      title: '',
      slug: '',
      content: '',
      excerpt: '',
      category_id: '',
      tags: '',
    },
  });

  // Reset form when article changes
  useEffect(() => {
    if (article) {
      reset({
        title: article.title,
        slug: article.slug,
        content: article.content,
        excerpt: article.excerpt ?? '',
        category_id: article.category_id ?? '',
        tags: article.tags?.join(', ') ?? '',
      });
    } else {
      reset({
        title: '',
        slug: '',
        content: '',
        excerpt: '',
        category_id: '',
        tags: '',
      });
    }
  }, [article, reset]);

  // Auto-generate slug from title
  const title = watch('title');
  useEffect(() => {
    if (!isEditing && title) {
      const slug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setValue('slug', slug);
    }
  }, [title, isEditing, setValue]);

  // Reset uploaded images when dialog closes
  useEffect(() => {
    if (!open) {
      setUploadedImages([]);
      setUploadError(null);
    }
  }, [open]);

  // Handle image upload
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Only PNG, JPEG, GIF, and WebP images are allowed');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be less than 5MB');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      // Generate unique filename with timestamp prefix
      const timestamp = Date.now();
      const fileName = `${timestamp}-${file.name.replace(/[^a-z0-9.]/gi, '-').toLowerCase()}`;

      const { error } = await supabase.storage
        .from('help-center')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('help-center')
        .getPublicUrl(fileName);

      setUploadedImages((prev) => [...prev, { name: file.name, url: publicUrl }]);
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Insert image markdown at cursor position
  const insertImageMarkdown = (url: string, altText: string) => {
    const markdown = `![${altText}](${url})`;
    const currentContent = watch('content');

    // Get textarea element to find cursor position
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent = currentContent.substring(0, start) + markdown + currentContent.substring(end);
      setValue('content', newContent);

      // Set cursor position after inserted text
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + markdown.length, start + markdown.length);
      }, 0);
    } else {
      // Fallback: append to end
      setValue('content', currentContent + '\n' + markdown);
    }
  };

  // Copy URL to clipboard
  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch {
      console.error('Failed to copy to clipboard');
    }
  };

  const saveMutation = useMutation({
    mutationFn: async (data: ArticleFormData) => {
      const tagsArray = data.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      const articleData = {
        title: data.title,
        slug: data.slug,
        content: data.content,
        excerpt: data.excerpt || null,
        category_id: data.category_id || null,
        tags: tagsArray,
        updated_at: new Date().toISOString(),
      };

      if (isEditing) {
        const { error } = await supabase
          .from('help_articles')
          .update(articleData)
          .eq('id', article.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('help_articles').insert({
          ...articleData,
          is_published: false,
          sort_order: 0,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['help-articles-admin'] });
      queryClient.invalidateQueries({ queryKey: ['help-articles'] });
      onClose();
    },
  });

  const onSubmit = (data: ArticleFormData) => {
    saveMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Article' : 'New Article'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the article details below.'
              : 'Create a new help article.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventEnterSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              {...register('title', { required: 'Title is required' })}
              placeholder="Article title"
            />
            {errors.title && (
              <p className="text-sm text-destructive">{errors.title.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              {...register('slug', { required: 'Slug is required' })}
              placeholder="article-slug"
            />
            {errors.slug && (
              <p className="text-sm text-destructive">{errors.slug.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select
              value={watch('category_id')}
              onValueChange={(value) => setValue('category_id', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="excerpt">Excerpt</Label>
            <Input
              id="excerpt"
              {...register('excerpt')}
              placeholder="Brief description"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="content">Content</Label>

            {/* Image Upload Section */}
            <div className="border rounded-lg p-3 bg-muted/30 space-y-3">
              <div className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Upload Images</span>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  <UploadIcon className="h-4 w-4 mr-2" />
                  {isUploading ? 'Uploading...' : 'Choose Image'}
                </Button>
                <span className="text-xs text-muted-foreground">
                  PNG, JPEG, GIF, WebP (max 5MB)
                </span>
              </div>

              {uploadError && (
                <p className="text-sm text-destructive">{uploadError}</p>
              )}

              {uploadedImages.length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs font-medium text-muted-foreground">Uploaded images:</span>
                  {uploadedImages.map((img, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm bg-background rounded p-2">
                      <img src={img.url} alt={img.name} className="h-8 w-8 object-cover rounded" />
                      <span className="truncate flex-1 text-xs">{img.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(img.url)}
                        title="Copy URL"
                      >
                        {copiedUrl === img.url ? (
                          <CheckIcon className="h-4 w-4 text-green-500" />
                        ) : (
                          <CopyIcon className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => insertImageMarkdown(img.url, img.name)}
                        title="Insert into content"
                      >
                        Insert
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Textarea
              id="content"
              {...register('content', { required: 'Content is required' })}
              ref={(e) => {
                register('content').ref(e);
                (textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = e;
              }}
              placeholder="Article content (Markdown supported)"
              className="min-h-[200px] font-mono text-sm"
            />
            {errors.content && (
              <p className="text-sm text-destructive">{errors.content.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="tags">Tags</Label>
            <Input
              id="tags"
              {...register('tags')}
              placeholder="tag1, tag2, tag3"
            />
            <p className="text-xs text-muted-foreground">
              Separate tags with commas
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
